import type { CellContext } from "@tanstack/react-table";
import type { VariantProps } from "class-variance-authority";
import type * as React from "react";

import { Badge, badgeVariants } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Icon, type IconSource } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import {
  DataTableCellContent,
  DataTableHoverActionsCell,
} from "./data-table-cell";
import type {
  DataTableColumnDef,
  DataTableColumnMeta,
} from "./data-table-types";
import { omitUndefined, type Omittable } from "../../lib/props";

// ---------------------------------------------------------------------------
// formatShortDate
// ---------------------------------------------------------------------------

/**
 * Format a date string as "Jan 5, 2025". Returns "—" for nullish values.
 */
export function formatShortDate(dateStr: string | null | undefined): string {
  if (dateStr == null) return "—";
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Badge map entry type
// ---------------------------------------------------------------------------

export type BadgeMapEntry = Omittable<{
  label: string;
  variant?: VariantProps<typeof badgeVariants>["variant"];
  size?: VariantProps<typeof badgeVariants>["size"];
}>;

// ---------------------------------------------------------------------------
// Column config types
// ---------------------------------------------------------------------------

type AccessorColumnConfig<TData> = Omittable<{
  accessorKey: keyof TData & string;
  header: string | React.ReactNode;
  enableSorting?: boolean;
  meta?: DataTableColumnMeta;
  cell?: (context: CellContext<TData, unknown>) => React.ReactNode;
}>;

type DisplayColumnConfig<TData> = Omittable<{
  id: string;
  header?: string | React.ReactNode;
  enableSorting?: boolean;
  meta?: DataTableColumnMeta;
  cell: (context: CellContext<TData, unknown>) => React.ReactNode;
}>;

type DateColumnConfig<TData> = Omittable<{
  accessorKey: keyof TData & string;
  header: string;
  enableSorting?: boolean;
}>;

type BadgeColumnConfig<TData> = {
  header: string;
  enableSorting?: boolean;
  badgeMap: Record<string, BadgeMapEntry>;
  fallback?: string;
  meta?: DataTableColumnMeta;
} & (
  | { accessorKey: keyof TData & string; id?: never; accessorFn?: never }
  | { id: string; accessorFn: (row: TData) => string; accessorKey?: never }
);

type HoverActionColumnConfig<TData> = Omittable<{
  accessorKey: keyof TData & string;
  header: string | React.ReactNode;
  enableSorting?: boolean;
  meta?: DataTableColumnMeta;
  content: (row: TData) => React.ReactNode;
  onClickAction: (row: TData) => void;
  actionLabel?: string | ((row: TData) => string);
  actionIcon?: IconSource;
  contentClickable?: boolean;
  showActionButton?: boolean;
}>;

// ---------------------------------------------------------------------------
// createColumnHelper
// ---------------------------------------------------------------------------

export function createColumnHelper<TData>() {
  return {
    /**
     * General-purpose column. If no `cell` provided, the hook's existing
     * `defaultColumn.cell` auto-wraps the accessor value in `DataTableCell`.
     */
    accessor(config: AccessorColumnConfig<TData>): DataTableColumnDef<TData> {
      const col: DataTableColumnDef<TData> = omitUndefined({
        accessorKey: config.accessorKey as string,
        header: config.header as string,
        enableSorting: config.enableSorting ?? false,
        meta: config.meta,
      });
      if (config.cell) {
        col.cell = config.cell;
      }
      return col;
    },

    /**
     * No accessor — consumer provides cell. For custom/complex cells.
     */
    display(config: DisplayColumnConfig<TData>): DataTableColumnDef<TData> {
      return omitUndefined({
        id: config.id,
        header: (config.header ?? "") as string,
        enableSorting: config.enableSorting ?? false,
        meta: config.meta,
        cell: config.cell,
      });
    },

    /**
     * Date column with built-in `formatShortDate`. Defaults to sortable
     * with subtle text brightness.
     */
    date(config: DateColumnConfig<TData>): DataTableColumnDef<TData> {
      return {
        accessorKey: config.accessorKey as string,
        header: config.header,
        enableSorting: config.enableSorting ?? true,
        meta: { minWidth: 120, textBrightness: "subtle" as const },
        cell: ({ row }) => {
          const value = (row.original as Record<string, unknown>)[
            config.accessorKey
          ] as string | null | undefined;
          return (
            <DataTableCellContent>
              <Text size={null} variant="muted" truncate>
                {formatShortDate(value)}
              </Text>
            </DataTableCellContent>
          );
        },
      };
    },

    /**
     * Maps accessor value to Badge rendering. Supports `accessorKey` or
     * `accessorFn + id` for derived values.
     */
    badge(config: BadgeColumnConfig<TData>): DataTableColumnDef<TData> {
      const cellFn = ({ row }: CellContext<TData, unknown>) => {
        let value: string;
        if (config.accessorKey) {
          value = String(
            (row.original as Record<string, unknown>)[config.accessorKey] ?? "",
          );
        } else {
          value = config.accessorFn!(row.original);
        }

        const entry =
          config.badgeMap[value] ??
          (config.fallback ? config.badgeMap[config.fallback] : undefined);

        if (!entry) return null;

        return (
          <DataTableCellContent>
            <Badge variant={entry.variant} size={entry.size}>
              {entry.label}
            </Badge>
          </DataTableCellContent>
        );
      };

      const shared = {
        header: config.header,
        enableSorting: config.enableSorting ?? false,
        meta: config.meta,
        cell: cellFn,
      };

      if (config.accessorKey) {
        return {
          ...shared,
          accessorKey: config.accessorKey as string,
        } as DataTableColumnDef<TData>;
      }

      return {
        ...shared,
        id: config.id!,
        accessorFn: config.accessorFn as (row: TData) => string,
      } as DataTableColumnDef<TData>;
    },

    /**
     * Name-style column: content + optional trailing action button.
     * Defaults to a gradient header hover treatment.
     */
    hoverAction(
      config: HoverActionColumnConfig<TData>,
    ): DataTableColumnDef<TData> {
      return {
        accessorKey: config.accessorKey as string,
        header: config.header as string,
        enableSorting: config.enableSorting ?? true,
        meta: {
          minWidth: 250,
          truncate: true,
          ...config.meta,
          headerHoverStyle: config.meta?.headerHoverStyle ?? "gradient",
        },
        cell: ({ row }) => {
          const data = row.original;
          const explicitLabel =
            typeof config.actionLabel === "function"
              ? config.actionLabel(data)
              : config.actionLabel;
          const rawValue = (data as Record<string, unknown>)[
            config.accessorKey
          ];
          const fallbackLabel =
            typeof rawValue === "string" && rawValue.trim().length > 0
              ? typeof config.header === "string" &&
                config.header.trim().length > 0
                ? `${config.header}: ${rawValue}`
                : rawValue
              : typeof config.header === "string" &&
                  config.header.trim().length > 0
                ? config.header
                : "Open details";
          const label = explicitLabel ?? fallbackLabel;
          const iconSource = config.actionIcon ?? "chevron-right";
          const content = config.content(data);
          const contentClickable = config.contentClickable ?? true;
          const showActionButton = config.showActionButton ?? false;

          return (
            <DataTableHoverActionsCell
              onClick={
                contentClickable ? () => config.onClickAction(data) : undefined
              }
              aria-label={contentClickable ? label : undefined}
              actions={
                showActionButton ? (
                  <Button
                    variant="outline"
                    size="icon-sm"
                    onClick={() => config.onClickAction(data)}
                    aria-label={label}
                  >
                    {typeof iconSource === "string" ? (
                      <Icon name={iconSource} color="muted" />
                    ) : (
                      <Icon as={iconSource} color="muted" />
                    )}
                  </Button>
                ) : null
              }
            >
              {content}
            </DataTableHoverActionsCell>
          );
        },
      };
    },
  };
}
